import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { WidgetService } from '@services/feature/widget.service';
import {
  ARM_WINDOW_MS,
  computeRevealScrollDelta,
  computeStepScrollDelta,
  CORRECTION_TOLERANCE_PX,
  DEBOUNCE_MS,
  GAP_PX,
  isRenderedTarget,
  MAX_CORRECTIONS,
  measureHostChrome,
  SETTLE_MAX_MS,
  SETTLE_POLL_MS,
  SETTLE_STABLE_POLLS
} from '@services/utils/host-page-scroll.util';

interface ScrollRequest {
  kind: 'step' | 'reveal';
  resolveTarget: () => HTMLElement | null;
}

const ACTIVATION_EVENTS = ['click', 'keydown'];
const ACTIVATION_KEYS = ['Enter', ' '];
const USER_SCROLL_EVENTS = ['wheel', 'touchmove', 'keydown'];
/** Scroll the page wherever focus is. */
const PAGE_SCROLL_KEYS = ['PageUp', 'PageDown', 'Home', 'End'];
/** Scroll the page only outside the widget; inside it they operate its controls. */
const CONTROL_SCROLL_KEYS = ['ArrowUp', 'ArrowDown', ' '];

/**
 * Web component only: scrolls the host page to the widget's next step or to content it just revealed.
 * The iframe embed keeps its parent postMessage and never reaches this service.
 *
 * - Enabled for every web-component embed, without a partner setting: the partner's
 *   `scroll_to_element_feature_is_enabled` only gates the iframe embed's slot-list parent postMessage
 *   (`getFreeAppointments`) and the classic widget; the iframe step message never read it.
 * - Armed only by a user activation inside the widget (a click, or Enter/Space), so boot, restored
 *   drafts, programmatic auto-skips and swipes never move the page. A new activation stops a running
 *   settle loop.
 * - Requests are debounced to the last one; a step outranks a reveal, also while it settles.
 * - After the scroll, a settle loop re-aligns while the host layout still moves, and stops when a
 *   scroll did not move the target at all.
 * - Wheel, touch and page-scroll keys cancel everything.
 */
@Injectable({
  providedIn: 'root',
})
export class HostPageScrollService {

  private widgetService = inject(WidgetService);
  private ngZone = inject(NgZone);

  private widgetHost: HTMLElement | null = null;
  private armedUntil = 0;
  private pending: ScrollRequest | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;
  /** Kind of the request the settle loop works on; only meaningful while `settleTimer` is set. */
  private settlingKind: ScrollRequest['kind'] | null = null;
  private listeningForUserScroll = false;

  private readonly onActivation = (event: Event): void => {
    if (!event.isTrusted || (event.type === 'keydown' && !ACTIVATION_KEYS.includes((event as KeyboardEvent).key))) {
      return;
    }
    // Capture phase, so this runs before the control's own handler. A running settle loop stops, so the
    // widget never fights the user. A request that has not fired yet stays: an activation that requests
    // nothing (a second click on a section that no longer reacts) must not swallow the first one's scroll.
    this.stopSettling();
    this.armedUntil = Date.now() + ARM_WINDOW_MS;
  }

  private readonly onUserScroll = (event: Event): void => {
    if (event.type === 'keydown' && !this.isPageScrollKey(event as KeyboardEvent)) {
      return;
    }
    this.cancel();
  }

  attach(widgetHost: HTMLElement, destroyRef: DestroyRef): void {
    if (!this.widgetService.isWebComponentMode) {
      return;
    }
    this.detach();
    this.widgetHost = widgetHost;
    this.ngZone.runOutsideAngular(() => {
      for (const type of ACTIVATION_EVENTS) {
        widgetHost.addEventListener(type, this.onActivation, { capture: true, passive: true });
      }
    });
    destroyRef.onDestroy(() => {
      if (this.widgetHost === widgetHost) {
        this.detach();
      }
    });
  }

  requestStep(resolveTarget: () => HTMLElement | null): void {
    this.request({ kind: 'step', resolveTarget });
  }

  requestReveal(resolveTarget: () => HTMLElement | null): void {
    this.request({ kind: 'reveal', resolveTarget });
  }

  private isEnabled(): boolean {
    return this.widgetService.isWebComponentMode === true;
  }

  private isArmed(): boolean {
    return Date.now() < this.armedUntil;
  }

  private isPageScrollKey(event: KeyboardEvent): boolean {
    if (PAGE_SCROLL_KEYS.includes(event.key)) {
      return true;
    }
    return CONTROL_SCROLL_KEYS.includes(event.key) && !event.composedPath().includes(this.widgetHost);
  }

  private request(request: ScrollRequest): void {
    if (!this.isEnabled() || !this.isArmed()) {
      return;
    }
    // A chain of programmatic steps caused by one activation stays armed.
    this.armedUntil = Date.now() + ARM_WINDOW_MS;
    if (request.kind === 'reveal' && this.settleTimer !== null && this.settlingKind === 'step') {
      return;
    }
    if (request.kind === 'step' || this.pending?.kind !== 'step') {
      this.pending = request;
    }
    this.schedule();
  }

  private schedule(): void {
    this.clearTimers();
    this.ngZone.runOutsideAngular(() => {
      this.listenForUserScroll();
      this.debounceTimer = setTimeout(() => this.fire(), DEBOUNCE_MS);
    });
  }

  private fire(): void {
    this.debounceTimer = null;
    const request = this.pending;
    this.pending = null;
    const target = request && this.widgetHost && this.isEnabled() && this.isArmed() ? request.resolveTarget() : null;
    // A hidden or auto-skipped step is no target; the arm stays for the next step of the chain.
    const delta = isRenderedTarget(target) ? this.desiredDelta(request, target, true) : 0;
    if (delta === 0) {
      this.stopListeningForUserScroll();
      return;
    }
    const topBeforeScroll = target.getBoundingClientRect().top;
    window.scrollTo({ top: Math.max(0, window.scrollY + delta), behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
    this.settle(request, topBeforeScroll);
  }

  /**
   * Re-aligns while the page still moves after the scroll (smooth scrolling, scroll anchoring, host
   * header transitions): once the target and the scroll position hold still for a few polls, a
   * remaining offset is corrected instantly, a bounded number of times. A scroll that did not move
   * the target (the widget scrolls inside a container of the host's own) ends the loop instead.
   */
  private settle(request: ScrollRequest, topBeforeScroll: number): void {
    this.settlingKind = request.kind;
    const startedAt = Date.now();
    let lastTop = NaN;
    let lastScrollY = NaN;
    let stablePolls = 0;
    let corrections = 0;

    const poll = (): void => {
      this.settleTimer = null;
      const target = request.resolveTarget();
      if (!isRenderedTarget(target) || !this.widgetHost || Date.now() - startedAt >= SETTLE_MAX_MS) {
        this.stopListeningForUserScroll();
        return;
      }
      const top = target.getBoundingClientRect().top;
      const delta = this.desiredDelta(request, target, false);
      stablePolls = Math.abs(top - lastTop) <= 1 && Math.abs(window.scrollY - lastScrollY) <= 1 ? stablePolls + 1 : 0;
      lastTop = top;
      lastScrollY = window.scrollY;

      if (stablePolls >= SETTLE_STABLE_POLLS) {
        const scrollMovedTarget = Math.abs(top - topBeforeScroll) > 1;
        if (!scrollMovedTarget || Math.abs(delta) <= CORRECTION_TOLERANCE_PX || corrections >= MAX_CORRECTIONS) {
          this.stopListeningForUserScroll();
          return;
        }
        corrections++;
        stablePolls = 0;
        topBeforeScroll = top;
        window.scrollTo({ top: Math.max(0, window.scrollY + delta), behavior: 'auto' });
      }
      this.settleTimer = setTimeout(poll, SETTLE_POLL_MS);
    };
    this.settleTimer = setTimeout(poll, SETTLE_POLL_MS);
  }

  /** The first scroll moves a step only if needed; the settle loop aligns it exactly. */
  private desiredDelta(request: ScrollRequest, target: HTMLElement, initial: boolean): number {
    const rect = target.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const chromeBottom = measureHostChrome(this.widgetHost, { width: window.innerWidth, height: viewportHeight });
    if (request.kind === 'reveal') {
      return computeRevealScrollDelta(rect, chromeBottom, viewportHeight);
    }
    return initial ? computeStepScrollDelta(rect, chromeBottom, viewportHeight) : rect.top - (chromeBottom + GAP_PX);
  }

  private prefersReducedMotion(): boolean {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Ends a running settle loop only; a pending request and its debounce are left alone. */
  private stopSettling(): void {
    if (this.settleTimer !== null) {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
      this.settlingKind = null;
      this.stopListeningForUserScroll();
    }
  }

  /**
   * Drops a pending request and a running settle loop, disarms, and releases the window listeners. Call it
   * before the widget scrolls somewhere itself (a validation error), so a pending host-page scroll cannot
   * override that scroll.
   */
  cancel(): void {
    this.armedUntil = 0;
    this.pending = null;
    this.clearTimers();
    this.stopListeningForUserScroll();
  }

  private detach(): void {
    this.cancel();
    if (this.widgetHost) {
      for (const type of ACTIVATION_EVENTS) {
        this.widgetHost.removeEventListener(type, this.onActivation, { capture: true });
      }
      this.widgetHost = null;
    }
  }

  private clearTimers(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.settleTimer !== null) {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
  }

  /** Window listeners exist only while a request is pending or settling. */
  private listenForUserScroll(): void {
    if (this.listeningForUserScroll) {
      return;
    }
    this.listeningForUserScroll = true;
    for (const type of USER_SCROLL_EVENTS) {
      window.addEventListener(type, this.onUserScroll, { capture: true, passive: true });
    }
  }

  private stopListeningForUserScroll(): void {
    if (!this.listeningForUserScroll || this.debounceTimer !== null || this.settleTimer !== null) {
      return;
    }
    this.listeningForUserScroll = false;
    for (const type of USER_SCROLL_EVENTS) {
      window.removeEventListener(type, this.onUserScroll, { capture: true });
    }
  }
}
